import { calendarIcon, searchIcon, checkIcon } from '@progress/kendo-svg-icons';
import { Component, NgZone, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment/moment';
import { saveAs } from 'file-saver';

import { EventService } from '../../core/services/event.service';
import { InvoiceService } from '../../services/invoice.service';
import { DashboardModel, BillingProgressModel, ResidentProgressModel, DashboardCustomerService } from '../../models/dashboard.model';
import { DashboardService, InvoicesSummary, PreBillSummary, WorkflowsSummary } from '../../services/dashboard.service';
import { InternalPropertyTeamModel } from '../../models/internal-property-team.model';
import { InvoiceModel } from '../../models/invoices.model';
import { faBell, faCalendar, faClock, faEnvelope, faSpinner, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ConfigService } from 'app/services/config.service';
import { OrderByPipe } from 'ngx-pipes';
import { SessionModel } from 'app/models/session.model';
import { MembershipService } from '../../core/services/membership.service';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { UserModel } from 'app/models/user.model';

@Component({
  standalone: false,
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  // Icons
  calendarIcon = calendarIcon;
  searchIcon = searchIcon;
  checkIcon = checkIcon;
  faCalendar = faCalendar;
  faEnvelope = faEnvelope;
  faSpinner = faSpinner;
  faClock = faClock;
  faBell = faBell;
  faSearch = faSearch;

  // Main dashboard models
  public dashboard_model: DashboardModel;
  public billing_progress: BillingProgressModel;
  public resident_progress: ResidentProgressModel;
  public customer_service: DashboardCustomerService;
  public invoices_summary: InvoicesSummary;
  public pre_bill_summary: PreBillSummary;
  public property_team: Array<InternalPropertyTeamModel>;
  public workflows_summary: WorkflowsSummary = new WorkflowsSummary();

  // UI state
  public customer_service_data = [];
  public customer_service_chart: any;
  public session: SessionModel = null;
  public viewInit: Boolean = false;
  public user_title: string;

  // Loading states
  public billingProgressLoading: boolean = true;
  public residentProgressLoading: boolean = true;
  public customerServiceLoading: boolean = true;
  public invoicesLoading: boolean = true;
  public preBillLoading: boolean = true;
  public propertyTeamLoading: boolean = true;
  public workflowsLoading: boolean = true;

  // Error states - only keep the one used for billing progress
  public billingProgressError: string = null;

  // Data availability flags
  public hasInvoicesData: boolean = false;
  public hasBillingProgressData: boolean = false;
  public hasResidentProgressData: boolean = false;
  public hasCustomerServiceData: boolean = false;
  public hasPreBillData: boolean = false;
  public hasPropertyTeamData: boolean = false;
  public hasWorkflowsData: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private config: ConfigService,
    private event_service: EventService,
    private invoice_service: InvoiceService,
    public membership: MembershipService,
    private zone: NgZone,
    private router: Router,
    private dashboardService: DashboardService
  ) {
    this.event_service.on('session-start', (model) => {
      this.session = model;
    });
    this.event_service.on('session-end', () => {
      this.session = null;
    });
    this.event_service.on('property-changed', (model) => {
      this.resetAllModules();
      this.loadDashboardData();
    });
  }

  ngOnInit() {
    // Initialize view immediately to show loading spinners
    this.viewInit = true;

    // Get session data
    this.membership.GetSession().subscribe(
      data => {
        this.session = data;
      },
      error => {
        this.session = null;
      });

    // Load dashboard data
    this.loadDashboardData();
  }

  private loadDashboardData(): void {
    // Load all modules simultaneously
    this.loadInvoicesData();
    this.loadBillingProgress();
    this.loadResidentProgress();
    this.loadCustomerServiceData();
    this.loadPreBillData();
    this.loadPropertyTeamData();
    this.loadWorkflowsData();
  }

  private resetAllModules(): void {
    // Reset all loading states to true to show spinners
    this.residentProgressLoading = true;
    this.billingProgressLoading = true;
    this.preBillLoading = true;
    this.workflowsLoading = true;
    this.invoicesLoading = true;
    this.propertyTeamLoading = true;
    this.customerServiceLoading = true;

    // Clear all data
    this.resident_progress = null;
    this.billing_progress = null;
    this.pre_bill_summary = null;
    this.workflows_summary = new WorkflowsSummary();
    this.invoices_summary = null;
    this.property_team = null;
    this.customer_service = null;

    // Clear error state
    this.billingProgressError = null;

    // Reset data availability flags
    this.hasInvoicesData = false;
    this.hasBillingProgressData = false;
    this.hasResidentProgressData = false;
    this.hasCustomerServiceData = false;
    this.hasPreBillData = false;
    this.hasPropertyTeamData = false;
    this.hasWorkflowsData = false;
  }
  
  private loadInvoicesData(): void {
    this.dashboardService.getInvoicesSummary().subscribe(
      data => {
        if (data && (data.current_invoice || (data.outstanding_invoices && data.outstanding_invoices.length > 0))) {
          this.invoices_summary = data;
          this.hasInvoicesData = true;
        } else {
          this.hasInvoicesData = false;
        }
        this.invoicesLoading = false;
      },
      error => {
        this.invoicesLoading = false;
        this.hasInvoicesData = false;
      });
  }
  
  private loadBillingProgress(): void {
    if (this.session?.current_property?.market?.type !== 'SingleFamily') {
      this.dashboardService.getBillingProgress().subscribe(
        data => {
          if (data && data.total_accounts > 0) {
            this.billing_progress = data;
            this.billingProgressError = null;
            this.hasBillingProgressData = true;
          } else {
            this.hasBillingProgressData = false;
          }
          this.billingProgressLoading = false;
        },
        error => {
          this.billingProgressError = 'Failed to load billing progress';
          this.billingProgressLoading = false;
          this.hasBillingProgressData = false;
        });
    }
    else {
      this.billingProgressError = 'Billing progress not available for Single Family properties';
      this.billingProgressLoading = false;
      this.hasBillingProgressData = false;
    }
  }

  private loadResidentProgress(): void {
    this.dashboardService.getResidentProgress().subscribe(
      data => {
        if (data && data.resident_count > 0) {
          this.resident_progress = data;
          this.hasResidentProgressData = true;
        } else {
          this.hasResidentProgressData = false;
        }
        this.residentProgressLoading = false;
      },
      error => {
        this.residentProgressLoading = false;
        this.hasResidentProgressData = false;
      });
  }
  
  private loadCustomerServiceData(): void {
    this.dashboardService.getCustomerService().subscribe(
      data => {
        if (data && data.day_summaries && data.day_summaries.length > 0) {
          this.customer_service = data;
          this.hasCustomerServiceData = true;
          // Draw charts when data is loaded
          setTimeout(() => this.drawCharts(), 100);
        } else {
          this.hasCustomerServiceData = false;
        }
        this.customerServiceLoading = false;
      },
      error => {
        this.customerServiceLoading = false;
        this.hasCustomerServiceData = false;
      });
  }
  
  private loadPreBillData(): void {
    this.dashboardService.getPreBillSummary().subscribe(
      data => {
        if (data && data.current_pre_bill) {
          this.pre_bill_summary = data;
          this.hasPreBillData = true;
          this.set_user_name();
        } else {
          this.hasPreBillData = false;
        }
        this.preBillLoading = false;
      },
      error => {
        this.preBillLoading = false;
        this.hasPreBillData = false;
      });
  }
  
  private loadPropertyTeamData(): void {
    this.dashboardService.getPropertyTeam().subscribe(
      data => {
        if (data && data.length > 0) {
          this.property_team = data;
          this.hasPropertyTeamData = true;
        } else {
          this.hasPropertyTeamData = false;
        }
        this.propertyTeamLoading = false;
      },
      error => {
        this.propertyTeamLoading = false;
        this.hasPropertyTeamData = false;
      });
  }
  
  private loadWorkflowsData(): void {
    this.dashboardService.getWorkflowsSummary().subscribe(
      data => {
        if (data && (data.workflows_count > 0 || data.pending_on_demand_efb_count > 0)) {
          this.workflows_summary = data;
          this.hasWorkflowsData = true;
        } else {
          this.hasWorkflowsData = false;
        }
        this.workflowsLoading = false;
      },
      error => {
        this.workflowsLoading = false;
        this.hasWorkflowsData = false;
      });
  }

  ngAfterViewInit() {
    this.viewInit=true;
    this.drawCharts();
  }

  ngOnDestroy() {
    this.zone.runOutsideAngular(() => {
        if (this.customer_service_chart) {
          this.customer_service_chart.dispose();
        }
    });
  }
  sendUserModel(user_model:UserModel){
      if(user_model){
         this.router.navigate(['/booking',{booking_url:user_model?.booking_url}],{skipLocationChange: true})
      }
  }

  // Utility methods for checking data availability
  hasBillingProgress(): boolean {
    return this.hasBillingProgressData && !!this.billing_progress && !this.billingProgressError;
  }

  hasResidentProgress(): boolean {
    return this.hasResidentProgressData && !!this.resident_progress;
  }

  hasCustomerService(): boolean {
    return this.hasCustomerServiceData && !!this.customer_service;
  }

  hasInvoices(): boolean {
    return this.hasInvoicesData && !!this.invoices_summary;
  }

  hasPreBill(): boolean {
    return this.hasPreBillData && !!this.pre_bill_summary;
  }

  hasPropertyTeam(): boolean {
    return this.hasPropertyTeamData && !!this.property_team && this.property_team.length > 0;
  }

  hasWorkflows(): boolean {
    return this.hasWorkflowsData && !!this.workflows_summary;
  }

    getWorkflowsCount(): number {
    return this.workflows_summary?.workflows_count ?? 0;
  }

  shouldShowNoWorkflows(): boolean {
    if (this.workflowsLoading) return false;
    return this.getWorkflowsCount() === 0;
  }

  shouldShowWorkflowsData(): boolean {
    if (this.workflowsLoading) return false;
    return this.shouldShowModule('workflows') && this.hasWorkflows() && this.getWorkflowsCount() > 0;
  }

  isWorkflowsPlural(): boolean {
    return this.getWorkflowsCount() > 1;
  }

  getWorkflowButtonText(): string {
    return this.getWorkflowsCount() <= 1 ? 'Begin Task' : 'Begin Tasks';
  }

  getPendingEfbCount(): number {
    return this.workflows_summary?.pending_on_demand_efb_count ?? 0;
  }

  hasFinalBillingApproval(): boolean {
    return this.session?.current_property?.final_billing_approval ?? false;
  }

  shouldShowNoEfbs(): boolean {
    if (this.workflowsLoading) return false;
    return this.getPendingEfbCount() === 0 || !this.hasFinalBillingApproval();
  }


  shouldShowEfbsData(): boolean {
    if (this.workflowsLoading) return false;
    return this.getPendingEfbCount() > 0 && this.hasFinalBillingApproval();
  }

  isEfbsPlural(): boolean {
    return this.getPendingEfbCount() > 1;
  }

  // Method to check if module should be shown
  shouldShowModule(moduleType: string): boolean {
    switch (moduleType) {
      case 'invoices':
        return this.invoicesLoading || this.hasInvoicesData;
      case 'billing-progress':
        return (this.billingProgressLoading || this.hasBillingProgressData);
      case 'resident-progress':
        return this.residentProgressLoading || this.hasResidentProgressData;
      case 'customer-service':
        return this.customerServiceLoading || this.hasCustomerServiceData;
      case 'pre-bill':
        return this.preBillLoading || this.hasPreBillData;
      case 'property-team':
        return this.propertyTeamLoading || this.hasPropertyTeamData;
      case 'workflows':
        return this.workflowsLoading || this.hasWorkflowsData;
      default:
        return false;
    }
  }



  createPdf() {
    if ( !this.invoices_summary?.current_invoice?.id) {
      return;
    }

    this.event_service.loadingStart();

    const invoiceId = this.invoices_summary?.current_invoice?.id;
    const invoiceNumber = this.invoices_summary?.current_invoice?.invoice_number;

    this.invoice_service.CreatePdf(invoiceId).subscribe(
      data => {
        this.event_service.loadingFinish();
        saveAs(data.url, "invoice_" + invoiceNumber + ".pdf");
      },
      error => {
        this.event_service.loadingFinish();
      });
  }

  addChartSeries(chart :am4charts.XYChart, valuey :string,color :string,labely :string){
    let series = chart.series.push(new am4charts.ColumnSeries());
    series.dataFields.valueY = valuey;
    series.dataFields.categoryX = "day";
    series.tooltip.autoTextColor = false;
    series.tooltip.label.fill = am4core.color(color);
    series.fill = am4core.color(color);
    series.strokeWidth = 0;
    series.clustered = false;
    series.sequencedInterpolation = true;
    series.stacked = true;
    series.tooltip.fontSize=11;
    series.tooltip.fontFamily="Verdana";
    series.tooltip.autoTextColor = false;
    series.tooltip.label.fill = am4core.color('#ffffff');
    series.tooltipHTML = labely+", {categoryX}: <b>{valueY}<b>";
  }

  drawCharts() {
    const customerServiceData = this.customer_service;

    if (this.viewInit && customerServiceData && customerServiceData.day_summaries && customerServiceData.day_summaries.length > 0) {
      this.customer_service_data = [];

      for (let day of new OrderByPipe().transform(customerServiceData.day_summaries, ['summary_date'])) {
        this.customer_service_data.push({
          'day': moment(day.summary_date).startOf('day').format('M/D'),
          'call_count': day.call_count,
          'email_count': day.email_count,
          'chat_count': day.chat_count
        });
      }
      
      this.zone.runOutsideAngular(() => {
        let chart = am4core.create("customerservice-chart", am4charts.XYChart);
        chart.data=this.customer_service_data;
        chart.fontSize=11;
        chart.fontFamily="Verdana";
        chart.paddingBottom=0;
        chart.marginBottom=0;
        chart.paddingLeft=0;
        chart.marginLeft=0;
        chart.height=am4core.percent(100);

        chart.cursor = new am4charts.XYCursor();
        chart.cursor.behavior = "none";
        chart.cursor.lineY.disabled = true;
        chart.cursor.lineX.disabled = true;

        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = "day";
        categoryAxis.renderer.baseGrid.disabled = false;
        categoryAxis.renderer.grid.template.disabled = true;
        categoryAxis.renderer.ticks.template.disabled = false;
        categoryAxis.renderer.ticks.template.strokeOpacity = 1;
        categoryAxis.renderer.ticks.template.stroke = am4core.color("#b2b2b2");
        categoryAxis.renderer.ticks.template.strokeWidth = 1;
        categoryAxis.renderer.ticks.template.length = 20;
        categoryAxis.renderer.ticks.template.location = 0; 
        categoryAxis.renderer.line.strokeOpacity = 1;
        categoryAxis.renderer.line.strokeWidth = 1;
        categoryAxis.renderer.line.stroke = am4core.color("#808080");
        categoryAxis.tooltip.label.fontSize = 11;
        categoryAxis.tooltip.label.fontFamily="Verdana";
        categoryAxis.tooltip.disabled = true;
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.minGridDistance = 30;
        categoryAxis.cursorTooltipEnabled = false;

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.min=0;
        valueAxis.renderer.baseGrid.disabled = false;
        valueAxis.renderer.grid.template.disabled = true;
        valueAxis.renderer.ticks.template.disabled = false;
        valueAxis.renderer.ticks.template.strokeOpacity = 1;
        valueAxis.renderer.ticks.template.stroke = am4core.color("#b2b2b2");
        valueAxis.renderer.ticks.template.strokeWidth = 1;
        valueAxis.renderer.ticks.template.length = 5;
        valueAxis.renderer.line.strokeOpacity = 1;
        valueAxis.renderer.line.strokeWidth = 1;
        valueAxis.renderer.line.stroke = am4core.color("#808080");
        valueAxis.tooltip.label.fontSize = 11;
        valueAxis.tooltip.label.fontFamily="Verdana";
        valueAxis.renderer.minGridDistance = 30;
        valueAxis.cursorTooltipEnabled = false;
        
        this.addChartSeries(chart,"call_count","#67b7dc","Calls");
        this.addChartSeries(chart,"email_count","#fdd400","Emails"); 
        this.addChartSeries(chart,"chat_count","#84b761","Chats");

        this.customer_service_chart=chart;
    });

    }
  }

  checkPastDue(invoice: InvoiceModel): boolean {
    var today_date = this.config.getCurrentMoment().startOf('day');
    return today_date.isAfter(invoice?.date_due) && !invoice.settled;
  }

  isBookingUrlAvailable(booking_url : string) : boolean
  {
    let available : boolean = true;
    if(booking_url === null || booking_url === undefined || booking_url === "")
    {
      available = false;
    }
    return available;
  }

  set_user_name() {
    const preBillMilestone = this.pre_bill_summary?.pre_bill_milestone;

    if (preBillMilestone?.user_activated?.position?.internal_property_team == true) {
      this.user_title = "SimpleBills Team";
    } else {
      this.user_title = `${preBillMilestone?.user_activated?.user_name_title || ''}`;
    }
  }
}
